import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sparkles,
  Check,
  Zap,
  Building2,
  ArrowUpRight,
  ArrowRight,
  CreditCard,
  ShieldAlert,
  Loader2,
  Info,
  Crown,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  active: { label: "Aktiv", tone: "success" },
  trialing: { label: "Testphase", tone: "primary" },
  past_due: { label: "Zahlung überfällig", tone: "warning" },
  unpaid: { label: "Unbezahlt", tone: "warning" },
  canceled: { label: "Gekündigt", tone: "muted" },
  incomplete: { label: "Unvollständig", tone: "warning" },
  incomplete_expired: { label: "Abgelaufen", tone: "muted" },
  paused: { label: "Pausiert", tone: "muted" },
  inactive: { label: "Kein Abonnement", tone: "muted" },
  override_active: { label: "Admin-Freischaltung", tone: "primary" },
};

const TONE = {
  success: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
  primary: "border-primary/40 text-primary bg-primary/10",
  warning: "border-amber-500/30 text-amber-400 bg-amber-500/10",
  muted: "border-white/10 text-muted-foreground bg-white/[0.03]",
};

function StatusBadge({ status }) {
  const meta = STATUS_LABELS[status] || { label: status || "—", tone: "muted" };
  return (
    <Badge
      variant="outline"
      className={TONE[meta.tone]}
      data-testid={`subscription-status-${status || "unknown"}`}
    >
      {meta.label}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function SubscriptionSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null); // plan_code being confirmed
  const [confirmPlan, setConfirmPlan] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/portal/subscription");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Show a success toast if we came back from Stripe checkout.
  useEffect(() => {
    const url = new URL(window.location.href);
    const state = url.searchParams.get("checkout");
    if (state === "success") {
      toast.success("Zahlung erfolgreich. Dein Abonnement wird gleich aktiv.");
      // Poll: subscription may take a few seconds to sync via webhook.
      const tries = [1000, 2500, 5000, 8000];
      tries.forEach((ms) => setTimeout(load, ms));
      url.searchParams.delete("checkout");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    } else if (state === "cancel") {
      toast.message("Zahlung abgebrochen. Kein Tarif geändert.");
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const currentPlan = data?.plan;
  const availablePlans = data?.available_plans || [];
  const stripeConfigured = data?.stripe_configured;

  const usage = data?.usage || {};
  const usagePct = useMemo(() => {
    if (!usage.location_limit) return 0;
    return Math.min(100, Math.round((usage.assigned_active / usage.location_limit) * 100));
  }, [usage.assigned_active, usage.location_limit]);

  const hasSubscription = Boolean(data?.stripe_subscription_id);
  const overrideActive = data?.override_active;

  const startCheckout = async (planCode) => {
    setProcessing(planCode);
    try {
      const { data: d } = await api.post("/portal/subscription/checkout", {
        plan_code: planCode,
      });
      if (d?.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      throw new Error("Keine Checkout-URL erhalten.");
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(null);
    }
  };

  const openBillingPortal = async () => {
    setProcessing("billing_portal");
    try {
      const { data: d } = await api.post("/portal/subscription/billing-portal", {
        return_url: window.location.origin + "/customer/konto",
      });
      if (d?.url) {
        window.location.href = d.url;
        return;
      }
      throw new Error("Keine Portal-URL erhalten.");
    } catch (e) {
      toast.error(formatApiError(e));
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div
        className="card-surface p-6 flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="subscription-section"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="subscription-section">
      {/* Current plan card */}
      <div className="card-surface p-5" data-testid="subscription-current-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Abonnement</h3>
          </div>
          <StatusBadge
            status={data.subscription_status || (overrideActive ? "override_active" : "inactive")}
          />
        </div>
        <Separator className="bg-white/[0.06] my-4" />

        {!stripeConfigured ? (
          <div
            data-testid="stripe-not-configured-banner"
            className="rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm p-3 flex items-start gap-3"
          >
            <ShieldAlert className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-medium">Stripe noch nicht eingerichtet.</div>
              <p className="mt-0.5 text-amber-200/80 text-xs">
                Sobald der Betreiber Stripe konfiguriert hat, kannst du hier deinen Tarif
                buchen und deine Rechnungen einsehen.
              </p>
            </div>
          </div>
        ) : null}

        <div className="grid sm:grid-cols-4 gap-4 mt-4">
          <InfoBlock
            label="Aktueller Tarif"
            value={currentPlan?.name || "—"}
            testId="subscription-plan-name"
          />
          <InfoBlock
            label="Monatlicher Preis"
            value={currentPlan?.price_display || "—"}
            testId="subscription-plan-price"
          />
          <InfoBlock
            label="Nächste Verlängerung"
            value={fmtDate(data.current_period_end)}
            testId="subscription-renewal"
          />
          <InfoBlock
            label="Standorte"
            value={`${usage.assigned_active || 0} / ${usage.location_limit || 0}`}
            testId="subscription-usage"
            hint={
              <span
                className={`text-[10px] ${
                  usage.over_limit ? "text-rose-400" : "text-muted-foreground"
                }`}
              >
                {usage.over_limit ? "Limit überschritten" : `${usagePct}% genutzt`}
              </span>
            }
          />
        </div>

        {data.cancel_at_period_end ? (
          <div
            className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2"
            data-testid="subscription-cancel-notice"
          >
            Dein Abo endet am {fmtDate(data.current_period_end)}. Danach steht das
            Kundenportal nicht mehr zur Verfügung.
          </div>
        ) : null}
        {data.trial_end && new Date(data.trial_end) > new Date() ? (
          <div
            className="mt-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-lg px-3 py-2"
            data-testid="subscription-trial-notice"
          >
            Testphase läuft bis {fmtDate(data.trial_end)}.
          </div>
        ) : null}
        {overrideActive ? (
          <div
            className="mt-4 text-xs text-primary bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 flex items-start gap-2"
            data-testid="subscription-override-notice"
          >
            <Crown className="w-3.5 h-3.5 mt-0.5" />
            <div>
              Dieser Tarif ist manuell durch das Team von {" "}
              <strong>1AI Agency</strong> aktiviert (Override: {data.plan_override}).
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex gap-2 flex-wrap">
          {hasSubscription && stripeConfigured ? (
            <Button
              onClick={openBillingPortal}
              className="rounded-full gap-2"
              disabled={processing === "billing_portal"}
              data-testid="subscription-manage-billing-btn"
            >
              {processing === "billing_portal" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Abrechnung verwalten
            </Button>
          ) : null}
        </div>
      </div>

      {/* Plans grid */}
      <div className="card-surface p-5" data-testid="subscription-plans-card">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Tarif wechseln</h3>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {availablePlans.map((plan) => {
            const isCurrent = plan.code === data?.plan_code;
            const disabled =
              !stripeConfigured || !plan.stripe_price_configured || processing !== null;
            return (
              <div
                key={plan.code}
                data-testid={`plan-card-${plan.code}`}
                className={`rounded-2xl border p-4 flex flex-col ${
                  isCurrent
                    ? "border-primary/40 bg-primary/[0.06]"
                    : "border-white/[0.06] bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold">{plan.name}</span>
                  {isCurrent ? (
                    <Badge
                      variant="outline"
                      className={TONE.primary}
                      data-testid={`plan-current-badge-${plan.code}`}
                    >
                      Aktueller Tarif
                    </Badge>
                  ) : null}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-heading text-3xl font-bold">
                    {plan.price_display}
                  </span>
                  <span className="text-xs text-muted-foreground">/ Monat</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Bis zu <strong>{plan.location_limit}</strong>{" "}
                  {plan.location_limit === 1 ? "Firma" : "Firmen"}
                </div>
                <ul className="mt-4 space-y-1.5 flex-1">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="text-xs flex items-start gap-1.5 text-muted-foreground"
                    >
                      <Check className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => setConfirmPlan(plan)}
                  disabled={disabled || isCurrent}
                  variant={isCurrent ? "ghost" : "default"}
                  className="mt-4 w-full rounded-full gap-2"
                  data-testid={`plan-select-btn-${plan.code}`}
                >
                  {processing === plan.code ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isCurrent ? (
                    "Bereits aktiv"
                  ) : (
                    <>
                      Wechseln
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </Button>
                {!plan.stripe_price_configured && stripeConfigured ? (
                  <p className="text-[10px] text-amber-400/80 mt-1 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Preis-ID fehlt in Server-Config
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-muted-foreground mt-4 flex items-start gap-1.5">
          <ShieldAlert className="w-3 h-3 mt-0.5" />
          Zahlungsdaten werden ausschließlich bei Stripe verarbeitet. Kartendaten
          erreichen unsere Server niemals.
        </p>
      </div>

      {/* Confirm dialog */}
      <AlertDialog
        open={!!confirmPlan}
        onOpenChange={(o) => (!o ? setConfirmPlan(null) : null)}
      >
        <AlertDialogContent data-testid="plan-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Tarifwechsel zu {confirmPlan?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Du wirst zu einem gesicherten Stripe-Checkout weitergeleitet. Der neue
              Tarif wird erst nach erfolgreicher Bestätigung durch Stripe aktiv.
              Monatlicher Preis: <strong>{confirmPlan?.price_display}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const code = confirmPlan.code;
                setConfirmPlan(null);
                startCheckout(code);
              }}
              data-testid="plan-confirm-checkout-btn"
            >
              Weiter zum Checkout <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InfoBlock({ label, value, testId, hint }) {
  return (
    <div data-testid={testId}>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 font-medium truncate">{value}</div>
      {hint ? <div className="mt-1">{hint}</div> : null}
    </div>
  );
}
